import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./index.css";
import { initWebVitals } from '@/lib/web-vitals';
import { restoreBrandTheme } from '@/lib/brand-theme';
import { registerServiceWorker } from '@/lib/register-sw';

try {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  // Sentry no-ops silently when the DSN is empty, which would leave production
  // with no error reporting at all and no indication of it. Say so once, at
  // boot, so the gap surfaces before someone goes looking for an error that was
  // never recorded. Fixed string — no user data. When the DSN is set, Vite
  // substitutes a truthy literal and this whole branch is dropped from the bundle.
  if (import.meta.env.PROD && !import.meta.env.VITE_SENTRY_DSN) {
    console.error(
      '[Sentry] VITE_SENTRY_DSN is not set in this production build — error ' +
        'reporting is DISABLED. Set it in the Vercel project environment variables.',
    );
  }

  // Apply cached brand accent color before first paint to prevent flicker
  restoreBrandTheme();

  createRoot(document.getElementById("root")!).render(<App />);

  // Initialize Web Vitals performance monitoring
  initWebVitals();

  // Register the PWA service worker (production only) so the app is installable.
  registerServiceWorker();
} catch (err) {
  console.error('Application startup failed:', err);
  const root = document.getElementById('root');
  if (root) {
    const container = document.createElement('div');
    container.style.cssText = 'min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;padding:2rem;';
    const inner = document.createElement('div');
    inner.style.cssText = 'max-width:28rem;text-align:center;';
    const h1 = document.createElement('h1');
    h1.style.cssText = 'font-size:1.25rem;font-weight:600;margin-bottom:0.5rem;';
    h1.textContent = 'Application Failed to Load';
    const p = document.createElement('p');
    p.style.cssText = 'color:#666;margin-bottom:1rem;';
    p.textContent = err instanceof Error ? err.message : String(err);
    const hint = document.createElement('p');
    hint.style.cssText = 'color:#999;font-size:0.875rem;';
    hint.textContent = 'If this is a preview deployment, ensure environment variables are configured in Vercel project settings.';
    const btn = document.createElement('button');
    btn.style.cssText = 'margin-top:1rem;padding:0.5rem 1rem;border:1px solid #ccc;border-radius:0.375rem;cursor:pointer;background:#fff;';
    btn.textContent = 'Reload';
    btn.onclick = () => location.reload();
    inner.append(h1, p, hint, btn);
    container.appendChild(inner);
    root.appendChild(container);
  }
}
